import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuthStore } from "../auth/auth.store";
import { api, ApiError } from "../lib/api";
import type { PublicUserDto } from "@gryzonie/shared";

export function LoginPage() {
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const data = await api<{ accessToken: string; user: PublicUserDto }>("/auth/login", {
        method: "POST",
        json: { email, password, rememberMe },
        skipAuth: true,
        skipRefresh: true,
      });
      setSession(data.accessToken, data.user);
      toast.success(`Witaj, ${data.user.displayName}!`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Nie udało się zalogować";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-base px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-xl border border-surface0 bg-mantle p-8 shadow-2xl"
      >
        <div className="mb-6 flex flex-col items-center gap-3">
          <img src="/GryzonieLogo.png" alt="Gryzonie" className="h-16 w-16" />
          <h1 className="text-2xl font-semibold">Gryzonie</h1>
          <p className="text-sm text-subtext0">Logowanie do panelu monitoringu</p>
        </div>

        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-subtext1">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
            className="w-full rounded border border-surface1 bg-surface0 px-3 py-2 text-text outline-none focus:border-primary"
          />
        </label>

        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-subtext1">Hasło</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="w-full rounded border border-surface1 bg-surface0 px-3 py-2 text-text outline-none focus:border-primary"
          />
        </label>

        <label className="mb-5 flex items-center gap-2 text-sm text-subtext1">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="h-4 w-4 accent-[var(--ctp-blue)]"
          />
          Pozostań zalogowany (30 dni)
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded bg-primary px-4 py-2 font-semibold text-crust transition hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? "Logowanie…" : "Zaloguj"}
        </button>
      </form>
    </div>
  );
}
